import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getBusinessStatus } from "../api/onboarding";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "ellyanna_rentals_feature_snapshot";
const EVENT_NAME = "ellyanna:rentals-feature-changed";

function normalizeSnapshot(snapshot = {}) {
  return {
    enabled: Boolean(snapshot.enabled),
    settingsReady: Boolean(snapshot.settingsReady),
    operationalReady: Boolean(snapshot.operationalReady),
    rentalMode: snapshot.rentalMode || "internal",
    discoverability: snapshot.discoverability || "private",
    marketplaceEnabled: Boolean(snapshot.marketplaceEnabled),
    defaultSalonId:
      snapshot.defaultSalonId === undefined || snapshot.defaultSalonId === null
        ? null
        : Number(snapshot.defaultSalonId) || null,
    defaultPolicySetId:
      snapshot.defaultPolicySetId === undefined ||
      snapshot.defaultPolicySetId === null
        ? null
        : Number(snapshot.defaultPolicySetId) || null,
    defaultBookingMode: snapshot.defaultBookingMode || "internal",
    defaultRequiresApproval: Boolean(snapshot.defaultRequiresApproval),
  };
}

function snapshotFromStatus(status = {}) {
  return {
    enabled: Boolean(status?.rentals_enabled),
    settingsReady: Boolean(status?.rentals_settings_ready),
    operationalReady: Boolean(status?.rentals_operational_ready),
    rentalMode: status?.rentals_mode || "internal",
    discoverability: status?.rentals_discoverability || "private",
    marketplaceEnabled: Boolean(status?.rentals_marketplace_enabled),
    defaultSalonId: status?.rentals_default_salon_id ?? null,
    defaultPolicySetId: status?.rentals_default_policy_set_id ?? null,
    defaultBookingMode: status?.rentals_default_booking_mode || "internal",
    defaultRequiresApproval: Boolean(
      status?.rentals_default_requires_approval
    ),
  };
}

function readSnapshot() {
  if (typeof window === "undefined") {
    return normalizeSnapshot();
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return normalizeSnapshot(raw ? JSON.parse(raw) : {});
  } catch {
    return normalizeSnapshot();
  }
}

export function publishRentalsFeatureSnapshot(snapshot = {}) {
  const normalized = normalizeSnapshot(snapshot);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: normalized }));
  }
  return normalized;
}

const RentalsFeatureContext = createContext({
  ...readSnapshot(),
  loading: false,
  error: "",
  refresh: async () => {},
});

export function RentalsFeatureProvider({ children }) {
  const { token, user } = useAuth();
  const salonId =
    user?.salon_id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null);
  const [state, setState] = useState(() => ({
    ...readSnapshot(),
    loading: Boolean(token),
    error: "",
  }));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleSnapshot = (event) => {
      const nextSnapshot = normalizeSnapshot(event.detail || readSnapshot());
      setState((prev) => ({
        ...prev,
        ...nextSnapshot,
        loading: false,
        error: "",
      }));
    };
    window.addEventListener(EVENT_NAME, handleSnapshot);
    return () => window.removeEventListener(EVENT_NAME, handleSnapshot);
  }, []);

  useEffect(() => {
    if (!token) {
      setState((prev) => ({
        ...prev,
        ...readSnapshot(),
        loading: false,
        error: "",
      }));
      return undefined;
    }

    let active = true;
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    getBusinessStatus(token, salonId)
      .then((status) => {
        if (!active) return;
        const snapshot = publishRentalsFeatureSnapshot(snapshotFromStatus(status));
        setState({
          ...snapshot,
          loading: false,
          error: "",
        });
      })
      .catch((err) => {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err?.message || "Unable to load rentals feature status.",
        }));
      });

    return () => {
      active = false;
    };
  }, [salonId, token]);

  const value = useMemo(
    () => ({
      ...state,
      refresh: async () => {
        if (!token) return;
        const status = await getBusinessStatus(token, salonId);
        const snapshot = publishRentalsFeatureSnapshot(snapshotFromStatus(status));
        setState({
          ...snapshot,
          loading: false,
          error: "",
        });
      },
    }),
    [salonId, state, token]
  );

  return (
    <RentalsFeatureContext.Provider value={value}>
      {children}
    </RentalsFeatureContext.Provider>
  );
}

export function useRentalsFeature() {
  return useContext(RentalsFeatureContext);
}
